/**
 * Cohort helpers — pure functions over the battlescape state (v0M3.3).
 *
 * Contains:
 *  - Tag queries (has `Melee` / `Ranged` / etc.).
 *  - Figure roster operations (alive counts, front-row queries, rank
 *    replacement after strike).
 *  - Facing operations (turn to face attacker, angle classification).
 *  - Adjacency classifiers (4-cardinal for combat + facing, 8-directional
 *    for morale rout effects per §2.7.8.6.e).
 *
 * All functions are pure w.r.t. the arguments they receive — those that
 * "mutate" (e.g. `turnToFace`, `compactFigures`) mutate their target in
 * place and are called by the round loop (`battle-round.ts`).
 */

import type { CohortOnField, Facing, Figure, Square } from './battle-state';

// ---------------------------------------------------------------------------
// Tag queries
// ---------------------------------------------------------------------------

export function hasTag(cohort: CohortOnField, tag: string): boolean {
  return cohort.tags.includes(tag);
}

export function isMelee(cohort: CohortOnField): boolean {
  return hasTag(cohort, 'Melee');
}

export function isRanged(cohort: CohortOnField): boolean {
  return hasTag(cohort, 'Ranged');
}

export function hasChargeResistance(cohort: CohortOnField): boolean {
  return hasTag(cohort, 'Charge Resistance');
}

// ---------------------------------------------------------------------------
// Figure roster queries
// ---------------------------------------------------------------------------

/** Count of living (not-killed) figures — includes wounded. */
export function aliveFigureCount(cohort: CohortOnField): number {
  let n = 0;
  for (const f of cohort.figures) if (!f.killed) n++;
  return n;
}

/** Count of front-row healthy (not wounded, not killed) figures.
 *  Session-27b (2026-07-24): front-rank width sourced from
 *  `cohort.stats.frontWidth` (xlsx-authoritative per cohort). */
export function healthyFrontRowCount(cohort: CohortOnField): number {
  let n = 0;
  const upTo = Math.min(cohort.stats.frontWidth, cohort.figures.length);
  for (let i = 0; i < upTo; i++) {
    const f = cohort.figures[i]!;
    if (!f.killed && !f.wounded) n++;
  }
  return n;
}

/**
 * Compact the roster after a strike: move killed figures to the tail,
 * promote healthy back-row figures to fill vacated front-row slots.
 *
 * This runs after §2.7.8.1.b sub-phase 4 outcomes resolve (per
 * "promotion happens after outcomes" rule). Wounded figures are moved
 * behind the front row but stay in the roster (they don't fight later
 * strikes this round).
 *
 * Ordering discipline (§2.7.8.13 rule c): iteration is left-to-right by
 * column, then front-to-back by rank. This function preserves that
 * order for the healthy figures.
 */
export function compactFigures(cohort: CohortOnField): void {
  // Partition figures into: front-eligible (healthy), reserves (wounded not-killed), dead.
  // The "front eligible" figures move to positions [0, k). The wounded go
  // after them. Killed figures move to the tail.
  const healthy: Figure[] = [];
  const wounded: Figure[] = [];
  const killed: Figure[] = [];
  for (const f of cohort.figures) {
    if (f.killed) killed.push(f);
    else if (f.wounded) wounded.push(f);
    else healthy.push(f);
  }
  cohort.figures = [...healthy, ...wounded, ...killed];
}

// ---------------------------------------------------------------------------
// Facing operations
// ---------------------------------------------------------------------------

/** Reset all cohorts' round-scoped flags at the start of a new round. */
export function beginRound(cohort: CohortOnField): void {
  cohort.facing.turnedThisRound = false;
  cohort.startSquare = cohort.position;
  cohort.movedThisRound = false;
  cohort.moveDirection = null;
  cohort.struckThisRound = false;
  cohort.attackedThisRound = false;
}

/**
 * Direction from `from` to an orthogonally-adjacent `to` square, or null
 * if not orthogonally adjacent.
 */
export function cardinalDirection(from: Square, to: Square): Facing | null {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (dx === 0 && dy === -1) return 'N';
  if (dx === 0 && dy === 1) return 'S';
  if (dx === 1 && dy === 0) return 'E';
  if (dx === -1 && dy === 0) return 'W';
  return null;
}

/**
 * Direction from `from` to `to` interpreted as a net-displacement
 * cardinal (matches §2.7.8.3.a rule for "same direction as movement").
 * Returns null if the vector isn't parallel to a cardinal axis.
 */
export function netCardinalDirection(from: Square, to: Square): Facing | null {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (dx === 0 && dy < 0) return 'N';
  if (dx === 0 && dy > 0) return 'S';
  if (dy === 0 && dx > 0) return 'E';
  if (dy === 0 && dx < 0) return 'W';
  return null;
}

/**
 * Turn defender to face `attackerSquare` if this is the first attacker
 * this round. Per §2.7.8.4.b: only the first attacker triggers the turn;
 * subsequent attackers hit whatever angle their approach square makes
 * relative to the already-updated facing.
 *
 * Returns `true` if the turn happened, `false` if the defender was
 * already facing something (or attackerSquare isn't cardinally adjacent).
 */
export function turnToFaceFirstAttacker(defender: CohortOnField, attackerSquare: Square): boolean {
  if (defender.facing.turnedThisRound) return false;
  const dir = cardinalDirection(defender.position, attackerSquare);
  if (dir === null) return false;
  defender.facing.direction = dir;
  defender.facing.turnedThisRound = true;
  return true;
}

// ---------------------------------------------------------------------------
// Angle classification (front / flank / rear from defender's facing)
// ---------------------------------------------------------------------------

export type AttackAngle = 'front' | 'flank' | 'rear';

/**
 * Classify the attacker's approach angle relative to the defender's
 * facing. Assumes attackerSquare is cardinally adjacent to defender —
 * caller must ensure this before calling. §2.7.8.4.c.
 */
export function classifyAngle(defenderFacing: Facing, attackerFromDefender: Facing): AttackAngle {
  if (defenderFacing === attackerFromDefender) return 'front';
  // Rear = opposite of facing.
  const opposite: Record<Facing, Facing> = { N: 'S', S: 'N', E: 'W', W: 'E' };
  if (opposite[defenderFacing] === attackerFromDefender) return 'rear';
  return 'flank';
}

/**
 * Convenience: classify the angle from the two cohorts' positions.
 * Requires that they are cardinally adjacent — returns 'front' as a
 * fallback if not, but callers should not rely on this for non-adjacent
 * cases.
 */
export function classifyAngleFromSquares(
  defender: CohortOnField,
  attacker: CohortOnField,
): AttackAngle {
  const dir = cardinalDirection(defender.position, attacker.position);
  if (dir === null) return 'front';
  return classifyAngle(defender.facing.direction, dir);
}

// ---------------------------------------------------------------------------
// Adjacency classifiers
// ---------------------------------------------------------------------------

/**
 * MFix4.3 — 8-directional (Chebyshev-1) adjacency for combat.
 *
 * Previously 4-cardinal only, which caused a "dancing" pathology: two
 * cohorts moving toward each other could each end their movement
 * cardinally-adjacent to the OTHER'S PRE-MOVE cell but only
 * diagonally-adjacent to each other's post-move cells — combat never
 * fires, next round both re-plan the same swap, battles run forever.
 *
 * Trace of the failure mode (before MFix4.3):
 *   Round N: A(4,6) targets pre-move B(5,7). A's X-primary step (5,6)
 *   would end cardinal-adjacent to B — but B simultaneously targets A's
 *   pre-move (4,6) and X-primary-steps to (4,7). End of round: A(5,6)
 *   B(4,7) → Chebyshev-1 diagonal, dx+dy=2, not cardinal. Combat skips.
 *
 * The 4-cardinal rule was a §2.7.3-era design choice from before
 * step-with-bump landed at v0M4.0j.2. With bump-and-cardinal-tie the
 * two systems interact pathologically; extending combat to Chebyshev-1
 * removes the dancing without any change to bump/pathing.
 *
 * The 8-directional morale-rout adjacency (`isMoraleAdjacent`) is
 * unchanged and remains the same predicate as this one — kept as two
 * names for call-site readability.
 */
export function isCombatAdjacent(a: Square, b: Square): boolean {
  const dx = Math.abs(a.x - b.x);
  const dy = Math.abs(a.y - b.y);
  return dx <= 1 && dy <= 1 && (dx + dy > 0);
}

/**
 * 8-directional adjacency (includes diagonals) — used for morale rout
 * effects (§2.7.8.6.e). Distinct from combat adjacency.
 */
export function isMoraleAdjacent(a: Square, b: Square): boolean {
  const dx = Math.abs(a.x - b.x);
  const dy = Math.abs(a.y - b.y);
  return dx <= 1 && dy <= 1 && !(dx === 0 && dy === 0);
}

/** Chebyshev distance — used for missile range (§2.7.8.2.b). */
export function chebyshevDistance(a: Square, b: Square): number {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}
